import React, { useState, useEffect } from 'react'
import {Edit2, Save, X, AlertCircle} from 'lucide-react'
import { lumi } from '../lib/lumi'

interface QuizResultText {
  _id: string
  level: string
  minScore: number
  maxScore: number
  title: string
  message: string
  recommendation: string
  color: string
  createdAt: string
  updatedAt: string
}

interface QuizResultTextsManagerProps {
  onClose: () => void
  embedded?: boolean
}

export function QuizResultTextsManager({ onClose, embedded }: QuizResultTextsManagerProps) {
  const [texts, setTexts] = useState<QuizResultText[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    recommendation: '',
    color: 'red'
  })

  useEffect(() => {
    fetchTexts()
  }, [])

  const fetchTexts = async () => {
    setLoading(true)
    try {
      const result = await lumi.entities.quiz_result_texts.list({
        sort: { minScore: 1 }
      })
      setTexts(result.list || [])
    } catch (error) {
      console.error('Error fetching result texts:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateText = async (textId: string) => {
    try {
      await lumi.entities.quiz_result_texts.update(textId, {
        title: formData.title,
        message: formData.message,
        recommendation: formData.recommendation,
        color: formData.color,
        updatedAt: new Date().toISOString()
      })
      await fetchTexts()
      setEditingId(null)
      resetForm()
    } catch (error) {
      console.error('Error updating text:', error)
      alert('❌ Erreur lors de la mise à jour')
    }
  }

  const startEdit = (text: QuizResultText) => {
    setEditingId(text._id)
    setFormData({
      title: text.title,
      message: text.message,
      recommendation: text.recommendation,
      color: text.color
    })
  }

  const resetForm = () => {
    setFormData({
      title: '',
      message: '',
      recommendation: '',
      color: 'red'
    })
    setEditingId(null)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Chargement...</div>
      </div>
    )
  }

  return (
    <div className={embedded ? '' : 'fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4'}>
      <div className={embedded ? 'w-full' : 'bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto'}>
        {!embedded && (
          <div className="sticky top-0 bg-gradient-to-r from-teal-600 to-cyan-600 text-white p-6 rounded-t-2xl flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">Gestion des Textes de Résultats</h2>
              <p className="text-teal-100 text-sm">Modifiez les messages affichés selon le score</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
        )}

        <div className="p-6">
          {/* Info Box */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">Système de score inversé</p>
              <p>Score bas (0-30) = besoin critique • Score moyen (31-60) = besoin modéré • Score élevé (61-100) = autonome</p>
            </div>
          </div>

          {/* Result Texts List */}
          <div className="space-y-6">
            {texts.map((text) => (
              <div key={text._id} className={`border-2 rounded-xl p-6 transition-all ${
                editingId === text._id ? 'border-teal-600 bg-teal-50' : 'border-gray-200 bg-white hover:border-gray-300'
              }`}>
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <span className={`px-4 py-2 rounded-full font-bold text-white ${
                      text.color === 'red' ? 'bg-red-500' :
                      text.color === 'orange' ? 'bg-orange-500' :
                      text.color === 'green' ? 'bg-green-500' :
                      'bg-blue-500'
                    }`}>
                      {text.minScore}-{text.maxScore} pts
                    </span>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">{text.level}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => editingId === text._id ? resetForm() : startEdit(text)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title={editingId === text._id ? "Annuler" : "Modifier"}
                  >
                    {editingId === text._id ? (
                      <X className="w-5 h-5 text-gray-600" />
                    ) : (
                      <Edit2 className="w-5 h-5 text-teal-600" />
                    )}
                  </button>
                </div>

                {editingId === text._id ? (
                  <form onSubmit={(e) => { e.preventDefault(); handleUpdateText(text._id) }} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Titre du résultat *</label>
                      <input
                        type="text"
                        required
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-600 focus:outline-none"
                        placeholder="Ex: Fort besoin d'accompagnement"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Message principal *</label>
                      <textarea
                        required
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-600 focus:outline-none resize-none"
                        rows={4}
                        placeholder="Description du niveau d'autonomie..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Recommandation *</label>
                      <textarea
                        required
                        value={formData.recommendation}
                        onChange={(e) => setFormData({ ...formData, recommendation: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-600 focus:outline-none resize-none"
                        rows={3}
                        placeholder="Recommandation d'accompagnement..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Couleur *</label>
                      <select
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-600 focus:outline-none"
                      >
                        <option value="red">Rouge (Critique)</option>
                        <option value="orange">Orange (Modéré)</option>
                        <option value="green">Vert (Autonome)</option>
                        <option value="blue">Bleu (Info)</option>
                      </select>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="submit"
                        className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all"
                      >
                        <Save className="w-5 h-5" />
                        Enregistrer
                      </button>
                      <button
                        type="button"
                        onClick={resetForm}
                        className="flex-1 bg-white text-gray-800 px-6 py-3 rounded-full font-semibold border-2 border-gray-200 hover:border-gray-300 transition-all"
                      >
                        Annuler
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{text.title}</h3>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-gray-600 mb-1">Message :</p>
                      <p className="text-gray-800">{text.message}</p>
                    </div>
                    <div className="bg-teal-50 border-l-4 border-teal-600 rounded-lg p-4">
                      <p className="text-sm font-medium text-teal-900 mb-1">💡 Recommandation :</p>
                      <p className="text-teal-800">{text.recommendation}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
